import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import localizationService from "../../services/localization-service";
import { validateSessionIdentity } from "../../actions/session.action";
import storageService from "../../services/storage-service";
import config from "../../config";

const MAX_ATTEMPTS = 5;
const LOCK_DURATION_MS = 10 * 60 * 1000; // 10 minutes

const ModalSessionIdentity = ({ isOpen, onClose, onSuccess, redirectUrl }) => {
  const selectedlang = useSelector((state) => state.account.lang);
  // True when the logged-in user has a Kipu account; controls whether DOB is required at session join.
  // Falls back to sessionStorage so the value survives a browser refresh (Redux is in-memory only).
  const hasKippuAccIdFromStore = useSelector(
    (state) => state.account.loggedUser?.HasKippuAccId,
  );
  const hasKippuAccId =
    hasKippuAccIdFromStore != null
      ? Boolean(hasKippuAccIdFromStore)
      : storageService.getStorageItem(config.HAS_KIPPU_ACC_ID) === "true";
  const userId = storageService.getStorageItem(config.USER_ID);
  const attemptsKey = `session_identity_attempts_${userId ?? "default"}`;
  const lockedAtKey = `session_identity_locked_at_${userId ?? "default"}`;

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!userId) return;

    const stored = localStorage.getItem(attemptsKey);
    const count = stored ? parseInt(stored, 10) : 0;

    if (count >= MAX_ATTEMPTS) {
      const lockedAt = parseInt(localStorage.getItem(lockedAtKey) ?? "0", 10);
      if (Date.now() - lockedAt >= LOCK_DURATION_MS) {
        localStorage.removeItem(attemptsKey);
        localStorage.removeItem(lockedAtKey);
        setAttempts(0);
        return;
      }
    }

    setAttempts(count);
  }, [userId]);

  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    localStorage.setItem(attemptsKey, attempts.toString());
  }, [userId, attempts, attemptsKey]);

  const locked = attempts >= MAX_ATTEMPTS;

  useEffect(() => {
    if (!locked) return;
    const lockedAt = parseInt(localStorage.getItem(lockedAtKey) ?? "0", 10);
    const remaining = LOCK_DURATION_MS - (Date.now() - lockedAt);
    if (remaining <= 0) {
      localStorage.removeItem(attemptsKey);
      localStorage.removeItem(lockedAtKey);
      setAttempts(0);
      return;
    }
    const timer = setTimeout(() => {
      localStorage.removeItem(attemptsKey);
      localStorage.removeItem(lockedAtKey);
      setAttempts(0);
    }, remaining);
    return () => clearTimeout(timer);
  }, [locked, attemptsKey, lockedAtKey]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (locked || loading) return;

    // iOS Safari blocks window.open() called after an await — the user-gesture
    // context is gone. Pre-open synchronously now and navigate it after the
    // validation resolves. Falls back to same-tab navigation if blocked.
    const popup = redirectUrl ? window.open("", "_blank") : null;

    setError(null);
    setLoading(true);

    try {
      const res = await validateSessionIdentity(
        firstName,
        lastName,
        dateOfBirth,
      );
      if (res.Valid) {
        setFirstName("");
        setLastName("");
        setDateOfBirth("");
        localStorage.removeItem(attemptsKey);
        localStorage.removeItem(lockedAtKey);
        setAttempts(0);
        if (redirectUrl) {
          if (popup && !popup.closed) {
            popup.location.href = redirectUrl;
          } else {
            window.location.href = redirectUrl;
          }
        }
        onSuccess();
      } else {
        if (popup && !popup.closed) popup.close();
        const newAttempts = attempts + 1;
        setAttempts(newAttempts);
        if (newAttempts >= MAX_ATTEMPTS) {
          localStorage.setItem(lockedAtKey, Date.now().toString());
          setError(null);
        } else {
          setError(
            selectedlang === "fr-CA"
              ? `Les informations fournies ne correspondent pas à nos dossiers. Il vous reste ${MAX_ATTEMPTS - newAttempts} tentative(s).`
              : `The information provided does not match our records. You have ${MAX_ATTEMPTS - newAttempts} attempt(s) remaining.`,
          );
        }
      }
    } catch {
      if (popup && !popup.closed) popup.close();
      setError(
        selectedlang === "fr-CA"
          ? "Une erreur est survenue. Veuillez réessayer."
          : "An error occurred. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFirstName("");
    setLastName("");
    setDateOfBirth("");
    setError(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {selectedlang === "fr-CA"
              ? "Vérification d'identité"
              : "Identity Verification"}
          </h3>
        </div>
        {locked ? (
          <>
            <div className="px-6 py-4">
              <p className="text-sm text-red-600 font-medium mb-2">
                {selectedlang === "fr-CA"
                  ? "Nombre maximum de tentatives atteint."
                  : "Maximum number of attempts reached."}
              </p>
              <p className="text-sm text-gray-600">
                {selectedlang === "fr-CA"
                  ? "Veuillez contacter le support Wagon à "
                  : "Please contact Wagon Support at "}
                <a
                  href="mailto:Wagonsupport@ehncanada.com"
                  className="text-[#9aa82d] underline font-medium"
                >
                  Wagonsupport@ehncanada.com
                </a>
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-center">
              <button
                onClick={handleClose}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {selectedlang === "fr-CA" ? "Fermer" : "Close"}
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 space-y-4">
              <p className="text-sm text-gray-500">
                {selectedlang === "fr-CA"
                  ? "Veuillez confirmer votre identité pour rejoindre la session."
                  : "Please confirm your identity to join the session."}
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {selectedlang === "fr-CA" ? "Prénom" : "First Name"}
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#9aa82d] focus:border-[#9aa82d] text-sm"
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {selectedlang === "fr-CA" ? "Nom de famille" : "Last Name"}
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#9aa82d] focus:border-[#9aa82d] text-sm"
                  required
                />
              </div>
              {hasKippuAccId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {selectedlang === "fr-CA"
                      ? "Date de naissance"
                      : "Date of Birth"}
                  </label>
                  <input
                    type="date"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#9aa82d] focus:border-[#9aa82d] text-sm"
                    required
                  />
                </div>
              )}
              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
            <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-center space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#9aa82d] hover:bg-[#7d8a1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9aa82d] disabled:opacity-50"
              >
                {loading
                  ? selectedlang === "fr-CA"
                    ? "Vérification..."
                    : "Verifying..."
                  : selectedlang === "fr-CA"
                    ? "Vérifier"
                    : "Verify"}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                {selectedlang === "fr-CA" ? "Annuler" : "Cancel"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ModalSessionIdentity;
